import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

type Alert = {
  id: string;
  severity: "staff" | "emergency";
  status: string;
  activator_id: string;
  created_at: string;
  message: string | null;
};

export function SosBanner() {
  const { currentOrg } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);

  useEffect(() => {
    if (!currentOrg) return;
    const orgId = currentOrg.id;
    const load = async () => {
      const { data } = await supabase
        .from("sos_alerts")
        .select("id, severity, status, activator_id, created_at, message")
        .eq("organization_id", orgId)
        .in("status", ["active", "acknowledged"])
        .order("created_at", { ascending: false })
        .limit(5);
      setAlerts((data ?? []) as Alert[]);
    };
    load();
    const ch = supabase
      .channel(`sos:${orgId}:${Math.random().toString(36).slice(2, 10)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "sos_alerts", filter: `organization_id=eq.${orgId}` }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [currentOrg]);

  if (alerts.length === 0) return null;
  const top = alerts[0];
  const isEmergency = top.severity === "emergency" || alerts.some((a) => a.severity === "emergency");

  return (
    <Link
      to="/sos"
      className={`block w-full px-4 py-2 text-sm font-semibold text-white shadow-md ${
        isEmergency ? "animate-pulse bg-destructive" : "bg-destructive/90"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-2">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate">
          {alerts.length} active SOS alert{alerts.length === 1 ? "" : "s"}
          {isEmergency ? " — EMERGENCY" : ""} · tap to respond
        </span>
        <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs uppercase">{top.status}</span>
      </div>
    </Link>
  );
}
